import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { BREAKPOINTS } from "src/breakpoints";
import { Skeleton } from "src/atoms/Skeleton";
import { cmsImageLoader } from "src/utils/cms-image-loader";
import { PictureContainer } from "./ResponsivePicture.styled";

type ImageLoader = (params: {
  src: string;
  width?: number;
  height?: number;
  quality?: number;
}) => string;

export type PictureVariant = {
  src: string;
  width?: number | null;
  height?: number | null;
};

type NativeImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  fetchpriority?: "high" | "low" | "auto";
};

type ResponsivePictureProps = {
  mobile: PictureVariant;
  tablet?: PictureVariant;
  desktop?: PictureVariant;
  alt?: string;
  sizes?: string;
  priority?: boolean;
  loader?: ImageLoader;
  widths?: number[];
  densities?: number[];
  height?: number;
  objectFit?: CSSProperties["objectFit"];
  withContainer?: boolean;
  fallbackAspectRatio?: string | number;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  className?: string;
  style?: CSSProperties;
};

// 2560 and 3840 both resolved to the same file for the 2240-wide banner
// masters, so one of them was a second charge for one result. 3840 is the one
// kept: Cloudflare never upscales, so the top rung always returns the master
// itself, and a handful of legacy hero images are 2856-3083 wide — capping at
// 2560 would have been the only place in the app that served less than the
// source holds.
const DEFAULT_WIDTHS = [420, 640, 828, 1080, 1200, 1440, 1920, 3840];

function isResizable(src: string): boolean {
  return Boolean(src) && !/^(data|blob):/i.test(src);
}

function aspectRatioVariant(variant: PictureVariant): string | undefined {
  return variant.width && variant.height
    ? `${variant.width} / ${variant.height}`
    : undefined;
}

/**
 * Builds the srcset for every remote image in the app.
 * See docs/tech/responsive-images.md for the modes and when to use which.
 */
function ResponsivePicture({
  mobile,
  tablet,
  desktop,
  alt = "",
  sizes,
  priority = false,
  loader = cmsImageLoader,
  widths = DEFAULT_WIDTHS,
  densities,
  height,
  objectFit = "cover",
  withContainer = true,
  fallbackAspectRatio = 2.5,
  onLoad,
  className,
  style,
}: ResponsivePictureProps) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const reportedRef = useRef(false);

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      reportedRef.current = true;
      setLoaded(true);
      onLoad?.(event);
    },
    [onLoad]
  );

  // An image restored from cache can finish before hydration attaches the
  // handler, so the load is reported from here instead.
  useEffect(() => {
    const image = imgRef.current;

    if (reportedRef.current || !image?.complete) {
      return;
    }

    reportedRef.current = true;
    setLoaded(true);
    onLoad?.({
      currentTarget: image,
    } as React.SyntheticEvent<HTMLImageElement>);
  }, [onLoad]);

  const densityCandidates =
    densities?.length && height
      ? densities.map((density) => ({ density, height: height * density }))
      : undefined;

  const buildSrcSet = (src: string): string | undefined => {
    if (!isResizable(src)) {
      return undefined;
    }

    if (densityCandidates) {
      return densityCandidates
        .map(
          (candidate) =>
            `${loader({ src, height: candidate.height })} ${candidate.density}x`
        )
        .join(", ");
    }

    return widths
      .map((width) => `${loader({ src, width })} ${width}w`)
      .join(", ");
  };

  const buildSrc = (src: string): string => {
    if (!isResizable(src)) {
      return src;
    }

    if (densityCandidates) {
      return loader({ src, height: densityCandidates[0].height });
    }

    return loader({ src, width: widths[widths.length - 1] });
  };

  const base = desktop || tablet || mobile;
  const tabletVariant = tablet || mobile;

  const hasMobileSource = Boolean(mobile.src) && mobile.src !== base.src;
  const hasTabletSource =
    Boolean(tabletVariant.src) && tabletVariant.src !== base.src;

  const baseSrcSet = buildSrcSet(base.src);
  const sourceSizes = densityCandidates ? undefined : sizes;

  const priorityAttr: NativeImgProps = priority
    ? { fetchpriority: "high" }
    : {};

  // Attribute order matters: React sets them in prop order, and WebKit starts
  // a fetch as each one lands on an image created client-side. `src` first
  // fetched the largest fallback, and `srcSet` before `sizes` fetched a
  // candidate for 100vw — both on top of the right one. Keep `loading`,
  // `sizes` and `srcSet` ahead of `src`, and `src` last.
  const image = (
    <img
      ref={imgRef}
      alt={alt}
      onLoad={handleLoad}
      className={withContainer ? undefined : className}
      style={withContainer ? undefined : style}
      decoding="async"
      loading={priority ? "eager" : "lazy"}
      {...priorityAttr}
      sizes={baseSrcSet ? sourceSizes : undefined}
      srcSet={baseSrcSet}
      src={buildSrc(base.src)}
    />
  );

  const picture =
    hasMobileSource || hasTabletSource ? (
      <picture>
        {hasMobileSource && (
          <source
            media={`(max-width: ${BREAKPOINTS.sm})`}
            sizes={sourceSizes}
            srcSet={buildSrcSet(mobile.src)}
          />
        )}
        {hasTabletSource && (
          <source
            media={`(max-width: ${BREAKPOINTS.md})`}
            sizes={sourceSizes}
            srcSet={buildSrcSet(tabletVariant.src)}
          />
        )}
        {image}
      </picture>
    ) : (
      image
    );

  if (!withContainer) {
    return picture;
  }

  const cssVars: Record<string, string | number> = {
    "--picture-aspect-ratio-fallback": fallbackAspectRatio,
    "--picture-img-opacity": priority || loaded ? 1 : 0,
    "--picture-img-transition": priority ? "none" : "opacity 300ms ease",
    "--picture-img-object-fit": objectFit || "cover",
  };

  const mobileRatio = aspectRatioVariant(mobile);
  const tabletRatio = aspectRatioVariant(tabletVariant);
  const desktopRatio = aspectRatioVariant(base);
  if (mobileRatio) cssVars["--picture-aspect-ratio-mobile"] = mobileRatio;
  if (tabletRatio) cssVars["--picture-aspect-ratio-tablet"] = tabletRatio;
  if (desktopRatio) cssVars["--picture-aspect-ratio-desktop"] = desktopRatio;

  return (
    <PictureContainer
      className={className}
      style={{ ...cssVars, ...style } as CSSProperties}
    >
      {!priority && !loaded && (
        <Skeleton style={{ position: "absolute", inset: 0 }} />
      )}
      {picture}
    </PictureContainer>
  );
}

export { ResponsivePicture };
